/**
 * PaymentPlanCard Component
 * One agency payment plan: balances, agreed terms, and payment history.
 *
 * Rendered as the detail panel expanded from a PaymentPlan batch row in BatchesToPayTab, or
 * standalone in that tab's "Payment plans" section for an active plan with no batch row yet
 * (e.g. a BSS plan staff haven't created a collection batch for). Paying goes through the ordinary batch checkout (the plan's collection batch is a real
 * batch), so the card is informational — the Void action on an individual logged payment
 * is the one thing it still writes, and `allowVoid` hides it for viewers whose permissions
 * couldn't complete the void anyway.
 */

import { useState, useMemo, useCallback } from 'react';
import { OathConfirmDialog } from '../../../shared/OathUI';
import { toast } from 'react-toastify';
import { voidAchPayment } from '../../../../services/oathPowerReportService';
import { formatCurrencyWithSymbol } from '../utils/formatters';
import { formatPlanTerms } from '../../../../services/oathPaymentPlanService';

/**
 * MM/DD/YYYY, tolerant of a null or unparseable date. Kept local rather than using the
 * shared formatDate: this card matches b360's '—' for an empty date, not 'N/A'.
 */
const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

// allowVoid: the void-ach endpoint requires READ:ELECTRONIC-PAYMENT, so the read-only
// Batches to Pay branch (shown precisely because the user lacks that slug) passes false —
// a visible Void button there could only ever 403.
// compact: payment history only (no header/progress) — the Payment Plans page (US 4396)
// renders its own summary above it.
const PaymentPlanCard = ({ plan, onVoided, allowVoid = true, compact = false }) => {
  const [voidingPayment, setVoidingPayment] = useState(null);
  const [isVoiding, setIsVoiding] = useState(false);

  const payments = useMemo(() => plan?.payments || [], [plan]);

  // Guard against a plan whose payments exceed its balance (overpayment is allowed):
  // the bar caps at 100% while the figures below still show the real numbers.
  const percentPaid = useMemo(() => {
    const starting = Number(plan?.startingBalance) || 0;
    if (starting <= 0) return 0;
    const paid = Number(plan?.totalPaid) || 0;
    return Math.min(100, Math.max(0, (paid / starting) * 100));
  }, [plan]);

  const terms = useMemo(() => formatPlanTerms(plan, formatCurrencyWithSymbol), [plan]);

  const handleVoidConfirm = useCallback(async () => {
    if (!voidingPayment?.reportId) return;

    setIsVoiding(true);
    const result = await voidAchPayment(voidingPayment.reportId, 'Voided by agent from payment plan');
    setIsVoiding(false);
    setVoidingPayment(null);

    if (result.success) {
      toast.success('Payment voided.');
      if (onVoided) onVoided();
    } else {
      toast.error(result.error || 'Failed to void payment.');
    }
  }, [voidingPayment, onVoided]);

  if (!plan) return null;

  const isPaidOff = plan.isPaidOff || (Number(plan.remainingBalance) || 0) <= 0;

  return (
    <div className="p-4 sm:p-5 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
      {/* Header: type + remaining balance */}
      {!compact && (
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {plan.name
                || (plan.typeName ? `${plan.typeName} payment plan` : 'Payment plan')}
            </span>
            {plan.name && plan.typeName && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {plan.typeName}
              </span>
            )}
            {isPaidOff && (
              <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                PAID OFF
              </span>
            )}
          </div>
          {terms && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{terms}</p>
          )}
          {plan.effectiveDate && (
            <p className="text-xs text-gray-500 dark:text-gray-500">
              Start date {formatDate(plan.effectiveDate)}
            </p>
          )}
        </div>

        {/* Labels below mirror b360's plan summary card (agenciesPaymentPlan.jsx) so staff
            and agents use the same words for the same figures on a phone call. */}
        <div className="text-right">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Remaining balance
          </p>
          <p className="text-2xl font-bold text-primary dark:text-primary-light">
            {formatCurrencyWithSymbol(plan.remainingBalance)}
          </p>
        </div>
      </div>

      )}

      {/* Progress */}
      {!compact && (
      <div className="mb-4">
        <div className="h-2 w-full rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
          <div
            className="h-full rounded-full bg-primary dark:bg-primary-light transition-[width] duration-500"
            style={{ width: `${percentPaid}%` }}
          />
        </div>
        <div className="mt-1.5 flex justify-between text-xs text-gray-600 dark:text-gray-400">
          <span>{formatCurrencyWithSymbol(plan.totalPaid)} paid</span>
          <span>{formatCurrencyWithSymbol(plan.startingBalance)} starting balance</span>
        </div>
      </div>
      )}

      {/* Payment history */}
      {compact && payments.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">No payments yet.</p>
      )}
      {payments.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
            Payments logged
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium text-right">Amount</th>
                  <th className="py-2 pr-4 font-medium">Source</th>
                  <th className="py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {payments.map((payment, index) => (
                  <tr
                    key={`${payment.reportId ?? 'manual'}-${payment.transactionDate}-${index}`}
                    className="border-b border-gray-100 dark:border-gray-700/50 last:border-0"
                  >
                    <td className="py-2 pr-4 text-gray-900 dark:text-white whitespace-nowrap">
                      {formatDate(payment.transactionDate)}
                    </td>
                    <td className="py-2 pr-4 text-right font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {formatCurrencyWithSymbol(payment.amount)}
                    </td>
                    <td className="py-2 pr-4 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                      {/* Already display text from the API ("Paid online" /
                          "Received by Bankers") — no code-to-label mapping needed. */}
                      {payment.method || '—'}
                    </td>
                    <td className="py-2 text-right">
                      {allowVoid && payment.canVoid && (
                        <button
                          type="button"
                          onClick={() => setVoidingPayment(payment)}
                          className="text-xs font-medium text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300"
                        >
                          Void
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <OathConfirmDialog
        open={!!voidingPayment}
        title="Void this payment?"
        message={
          voidingPayment
            ? `This cancels the ${formatCurrencyWithSymbol(voidingPayment.amount)} ACH payment and adds the amount back to your plan balance. It can only be voided before the payment is sent to the bank.`
            : ''
        }
        confirmLabel={isVoiding ? 'Voiding…' : 'Void Payment'}
        cancelLabel="Keep Payment"
        variant="warning"
        onConfirm={handleVoidConfirm}
        onCancel={() => setVoidingPayment(null)}
      />
    </div>
  );
};

export default PaymentPlanCard;
